// src/components/ProfilePage.jsx - FIXED DEPARTMENT DISPLAY
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import api from '../services/api';
import { getMediaUrl } from '../utils/mediaUrl';
import DashboardLayout from './shared/DashboardLayout';
import { notify, confirmDialog } from '../utils/notify';
import '../styles/Profile.css';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone_number: user?.phone_number || user?.profile?.phone_number || '',
    date_of_birth: user?.profile?.date_of_birth || '',
    gender: user?.profile?.gender || '',
    address: user?.profile?.address || '',
    emergency_contact: user?.profile?.emergency_contact || '',
    blood_group: user?.profile?.blood_group || '',
    // Student fields
    enrollment_year: user?.profile?.enrollment_year || '',
    current_semester: user?.profile?.current_semester || '',
    course: user?.profile?.course || '',
    entrance_score: user?.profile?.entrance_score || '',
    student_id: user?.profile?.student_id || '',
    student_department: user?.profile?.student_department || '',
    // Faculty/Staff fields
    faculty_department: user?.profile?.faculty_department || '',
    faculty_subrole: user?.profile?.faculty_subrole || '',
    employee_id: user?.profile?.employee_id || '',
  });

  const [bankData, setBankData] = useState({
      account_holder_name: '',
      bank_name: '',
      account_number: '',
      ifsc_code: '',
      branch: '',
  });

  const [bankLoading, setBankLoading] = useState(false);
  const [bankMessage, setBankMessage] = useState({ type: '', text: '' });

  // Helper function to get display name for faculty sub-role
  const getFacultySubroleDisplay = (subrole) => {
    const subroleMap = {
      'SENIOR_RESIDENT': 'Senior Resident',
      'ASSISTANT_PROFESSOR': 'Assistant Professor',
      'ASSOCIATE_PROFESSOR': 'Associate Professor',
      'PROFESSOR': 'Professor',
      'HEAD_OF_DEPARTMENT': 'Head of Department',
      'PROFESSOR_HOD': 'Professor + HOD',
      'DEAN': 'Dean',
    };
    return subroleMap[subrole] || subrole;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'Please select an image file' });
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Image size should be less than 5MB' });
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async () => {
    const file = fileInputRef.current?.files[0];
    if (!file) {
      setMessage({ type: 'error', text: 'Please select an image first' });
      return;
    }

    try {
      setLoading(true);
      const updatedUser = await authAPI.uploadProfilePicture(file);
      updateUser(updatedUser);
      setImagePreview(null);
      setMessage({ type: 'success', text: 'Profile picture updated successfully' });
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to upload profile picture',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageDelete = async () => {
    if (!await confirmDialog('Are you sure you want to delete your profile picture?')) {
      return;
    }

    try {
      setLoading(true);
      const updatedUser = await authAPI.deleteProfilePicture();
      updateUser(updatedUser);
      setImagePreview(null);
      setMessage({ type: 'success', text: 'Profile picture deleted successfully' });
    } catch (error) {
      console.error('Error deleting profile picture:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to delete profile picture',
      });
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
      const loadBankDetail = async () => {
        try {
          const data = await authAPI.getBankDetail();
          setBankData({
            account_holder_name: data.account_holder_name || '',
            bank_name: data.bank_name || '',
            account_number: data.account_number || '',
            ifsc_code: data.ifsc_code || '',
            branch: data.branch || '',
          });
        } catch (err) {
          console.error('Error loading bank detail:', err);
        }
      };
      loadBankDetail();
    }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    try {
      setLoading(true);

      // Create FormData for sending all data including profile fields
      const formDataToSend = new FormData();

      // Add user-level fields
      formDataToSend.append('first_name', formData.first_name);
      formDataToSend.append('last_name', formData.last_name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone_number', formData.phone_number || '');

      // Add profile fields with 'profile.' prefix
      formDataToSend.append('profile.date_of_birth', formData.date_of_birth || '');
      formDataToSend.append('profile.gender', formData.gender || '');
      formDataToSend.append('profile.address', formData.address || '');
      formDataToSend.append('profile.emergency_contact', formData.emergency_contact || '');
      formDataToSend.append('profile.blood_group', formData.blood_group || '');

      // Add role-specific fields
      if (user.role === 'STUDENT') {
        formDataToSend.append('profile.enrollment_year', formData.enrollment_year || '');
        formDataToSend.append('profile.current_semester', formData.current_semester || '');
        formDataToSend.append('profile.course', formData.course || '');
        formDataToSend.append('profile.entrance_score', formData.entrance_score || '');
        formDataToSend.append('profile.student_id', formData.student_id || '');
        // Only send student_department for non-MBBS students
        if (formData.course !== 'MBBS') {
          formDataToSend.append('profile.student_department', formData.student_department || '');
        }
      } else if (['FACULTY', 'ADMIN', 'FINANCE', 'HOSTEL_ADMIN', 'LIBRARY_STAFF', 'TRANSPORT_MANAGER', 'DRIVER'].includes(user.role)) {
        formDataToSend.append('profile.faculty_department', formData.faculty_department || '');
        formDataToSend.append('profile.employee_id', formData.employee_id || '');
        // Don't send designation, it's derived from faculty_subrole
      }

      // Use axios directly with FormData
      const response = await api.patch('/auth/profile/', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      updateUser(response.data);

      // Update form data with the returned data to ensure sync
      setFormData({
        first_name: response.data.first_name || '',
        last_name: response.data.last_name || '',
        email: response.data.email || '',
        phone_number: response.data.phone_number || '',
        date_of_birth: response.data.profile?.date_of_birth || '',
        gender: response.data.profile?.gender || '',
        address: response.data.profile?.address || '',
        emergency_contact: response.data.profile?.emergency_contact || '',
        blood_group: response.data.profile?.blood_group || '',
        enrollment_year: response.data.profile?.enrollment_year || '',
        current_semester: response.data.profile?.current_semester || '',
        course: response.data.profile?.course || '',
        entrance_score: response.data.profile?.entrance_score || '',
        student_id: response.data.profile?.student_id || '',
        student_department: response.data.profile?.student_department || '',
        faculty_department: response.data.profile?.faculty_department || '',
        faculty_subrole: response.data.profile?.faculty_subrole || '',
        employee_id: response.data.profile?.employee_id || '',
      });

      setIsEditing(false);
      setMessage({ type: 'success', text: 'Profile updated successfully' });
    } catch (error) {
      console.error('Error updating profile:', error);
      console.error('Error response:', error.response?.data);

      // Extract detailed error message
      let errorMessage = 'Failed to update profile';
      if (error.response?.data) {
        if (typeof error.response.data === 'object') {
          // Handle field-specific errors
          const errors = Object.entries(error.response.data)
            .map(([field, messages]) => {
              const message = Array.isArray(messages) ? messages[0] : messages;
              return `${field}: ${message}`;
            })
            .join(', ');
          errorMessage = errors || errorMessage;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
      }

      setMessage({
        type: 'error',
        text: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form to original values
    setFormData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      phone_number: user?.phone_number || '',
      date_of_birth: user?.profile?.date_of_birth || '',
      gender: user?.profile?.gender || '',
      address: user?.profile?.address || '',
      emergency_contact: user?.profile?.emergency_contact || '',
      blood_group: user?.profile?.blood_group || '',
      enrollment_year: user?.profile?.enrollment_year || '',
      current_semester: user?.profile?.current_semester || '',
      course: user?.profile?.course || '',
      entrance_score: user?.profile?.entrance_score || '',
      student_id: user?.profile?.student_id || '',
      student_department: user?.profile?.student_department || '',
      faculty_department: user?.profile?.faculty_department || '',
      faculty_subrole: user?.profile?.faculty_subrole || '',
      employee_id: user?.profile?.employee_id || '',
    });
    setIsEditing(false);
    setMessage({ type: '', text: '' });
  };

  const handleBankChange = (e) => {
      const { name, value } = e.target;
      setBankData((prev) => ({ ...prev, [name]: value }));
    };

    const handleBankSubmit = async (e) => {
      e.preventDefault();
      setBankMessage({ type: '', text: '' });
      try {
        setBankLoading(true);
        await authAPI.saveBankDetail(bankData);
        setBankMessage({ type: 'success', text: 'Bank details saved successfully' });
      } catch (err) {
        setBankMessage({
          type: 'error',
          text: err.response?.data?.error || 'Failed to save bank details',
        });
      } finally {
        setBankLoading(false);
      }
    };

  const getInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    return user?.username?.substring(0, 2).toUpperCase() || 'U';
  };

  const getDashboardRoute = () => {
    const roleRoutes = {
      ADMIN: '/admin-dashboard',
      STUDENT: '/student-dashboard',
      FACULTY: '/faculty-dashboard',
      FINANCE: '/finance-dashboard',
      HOSTEL_ADMIN: '/hostel-dashboard',
      LIBRARY_STAFF: '/library-dashboard',
      TRANSPORT_MANAGER: '/transport-dashboard',
      DRIVER: '/driver-dashboard',
    };
    return roleRoutes[user?.role] || '/login';
  };

  return (
    <DashboardLayout title="My Profile">
      <div className="profile-container">
        {message.text && (
          <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              {message.type === 'success' ? (
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              ) : (
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293z"
                  clipRule="evenodd"
                />
              )}
            </svg>
            {message.text}
          </div>
        )}

        <div className="profile-grid">
          {/* Profile Picture Section */}
          <div className="profile-picture-card card">
            <div className="card-body">
              <div className="profile-picture-section">
                <div className="profile-picture-large">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" />
                  ) : user?.profile_picture ? (
                    <img src={getMediaUrl(user.profile_picture)} alt="Profile" />
                  ) : (
                    <div className="profile-avatar-large">{getInitials()}</div>
                  )}
                </div>

                <div className="profile-picture-info">
                  <h3>
                    {user?.first_name} {user?.last_name}
                  </h3>
                  <p className="profile-role-badge">
                    <span className={`badge badge-blue`}>
                      {user?.role === 'FACULTY' && user?.profile?.faculty_subrole
                        ? getFacultySubroleDisplay(user.profile.faculty_subrole)
                        : user?.role}
                    </span>
                  </p>
                  <p className="profile-email">{user?.email}</p>
                </div>

                <div className="profile-picture-actions">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageSelect}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />

                  {imagePreview ? (
                    <div className="button-group-vertical">
                      <button
                        className="btn-primary"
                        onClick={handleImageUpload}
                        disabled={loading}
                      >
                        {loading ? 'Uploading...' : 'Upload Picture'}
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={() => {
                          setImagePreview(null);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                        }}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="button-group-vertical">
                      <button
                        className="btn-primary"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={loading}
                      >
                        {user?.profile_picture ? 'Change Picture' : 'Upload Picture'}
                      </button>
                      {user?.profile_picture && (
                        <button
                          className="btn-danger"
                          onClick={handleImageDelete}
                          disabled={loading}
                        >
                          Delete Picture
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Profile Details Section */}
          <div className="profile-details-card card">
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button
                  className="btn-icon-back"
                  onClick={() => navigate(getDashboardRoute())}
                  title="Back to Dashboard"
                  style={{
                    background: 'none',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    padding: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                    e.currentTarget.style.borderColor = '#9ca3af';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                    />
                  </svg>
                </button>
                <h2 style={{ margin: 0 }}>Profile Information</h2>
              </div>
              {!isEditing ? (
                <button className="btn-primary" onClick={() => setIsEditing(true)}>
                  Edit Profile
                </button>
              ) : (
                <div className="button-group">
                  <button className="btn-success" onClick={handleSubmit} disabled={loading}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button className="btn-secondary" onClick={handleCancel} disabled={loading}>
                    Cancel
                  </button>
                </div>
              )}
            </div>

            <div className="card-body">
              <form onSubmit={handleSubmit}>
                {/* Basic Information */}
                <div className="profile-section">
                  <h3 className="section-title">Basic Information</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Username</label>
                      <input
                        type="text"
                        className="form-input"
                        value={user?.username || ''}
                        disabled
                      />
                    </div>

                    <div className="form-group">
                      <label>Role</label>
                      <input
                        type="text"
                        className="form-input"
                        value={
                          user?.role === 'FACULTY' && user?.profile?.faculty_subrole
                            ? getFacultySubroleDisplay(user.profile.faculty_subrole)
                            : user?.role || ''
                        }
                        disabled
                      />
                    </div>

                    <div className="form-group">
                      <label>First Name</label>
                      <input
                        type="text"
                        name="first_name"
                        className="form-input"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Last Name</label>
                      <input
                        type="text"
                        name="last_name"
                        className="form-input"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Date of Birth</label>
                      <input
                        type="date"
                        name="date_of_birth"
                        className="form-input"
                        value={formData.date_of_birth}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      />
                    </div>

                    <div className="form-group">
                      <label>Gender</label>
                      <select
                        name="gender"
                        className="form-input"
                        value={formData.gender}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      >
                        <option value="">Select Gender</option>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                        <option value="O">Other</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Blood Group</label>
                      <select
                        name="blood_group"
                        className="form-input"
                        value={formData.blood_group}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      >
                        <option value="">Select Blood Group</option>
                        <option value="A+">A+</option>
                        <option value="A-">A-</option>
                        <option value="B+">B+</option>
                        <option value="B-">B-</option>
                        <option value="AB+">AB+</option>
                        <option value="AB-">AB-</option>
                        <option value="O+">O+</option>
                        <option value="O-">O-</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="profile-section">
                  <h3 className="section-title">Contact Information</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        name="email"
                        className="form-input"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Phone Number</label>
                      <input
                        type="tel"
                        name="phone_number"
                        className="form-input"
                        value={formData.phone_number}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      />
                    </div>

                    <div className="form-group">
                      <label>Emergency Contact</label>
                      <input
                        type="tel"
                        name="emergency_contact"
                        className="form-input"
                        value={formData.emergency_contact}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      />
                    </div>

                    <div className="form-group form-group-full">
                      <label>Address</label>
                      <textarea
                        name="address"
                        className="form-textarea"
                        rows="3"
                        value={formData.address}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      />
                    </div>
                  </div>
                </div>

                {/* Role-Specific Information */}
                {user?.role === 'STUDENT' && (
                  <div className="profile-section">
                    <h3 className="section-title">Academic Information</h3>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Student ID</label>
                        <input
                          type="text"
                          name="student_id"
                          className="form-input"
                          value={formData.student_id}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                        />
                      </div>

                      <div className="form-group">
                        <label>Course</label>
                        <input
                          type="text"
                          name="course"
                          className="form-input"
                          value={formData.course}
                          onChange={handleInputChange}
                          disabled
                        />
                      </div>

                      {/* FIXED: Show department NAME for non-MBBS students */}
                      {formData.course !== 'MBBS' && (
                        <div className="form-group">
                          <label>Department</label>
                          <input
                            type="text"
                            className="form-input"
                            value={user?.profile?.student_department_name || 'Not assigned'}
                            disabled
                          />
                        </div>
                      )}

                      <div className="form-group">
                        <label>Enrollment Year</label>
                        <input
                          type="number"
                          name="enrollment_year"
                          className="form-input"
                          value={formData.enrollment_year}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          min="1900"
                          max="2100"
                        />
                      </div>

                      <div className="form-group">
                        <label>Current Semester</label>
                        <input
                          type="number"
                          name="current_semester"
                          className="form-input"
                          value={formData.current_semester}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          min="1"
                          max="12"
                        />
                      </div>

                      <div className="form-group">
                        <label>Entrance Score</label>
                        <input
                          type="number"
                          name="entrance_score"
                          className="form-input"
                          value={formData.entrance_score}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          min="0"
                          max="100"
                          step="0.01"
                        />
                      </div>

                      {/* Show assigned professor name if available */}
                      {user?.profile?.assigned_professor_name && (
                        <div className="form-group">
                          <label>Assigned Professor</label>
                          <input
                            type="text"
                            className="form-input"
                            value={user.profile.assigned_professor_name}
                            disabled
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {['FACULTY', 'ADMIN', 'FINANCE', 'HOSTEL_ADMIN', 'LIBRARY_STAFF', 'TRANSPORT_MANAGER', 'DRIVER'].includes(
                  user?.role
                ) && (
                  <div className="profile-section">
                    <h3 className="section-title">Professional Information</h3>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Employee ID</label>
                        <input
                          type="text"
                          name="employee_id"
                          className="form-input"
                          value={formData.employee_id}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                        />
                      </div>

                      {/* FIXED: Show department NAME for faculty */}
                      <div className="form-group">
                        <label>Department</label>
                        <input
                          type="text"
                          className="form-input"
                          value={user?.profile?.faculty_department_name || 'Not assigned'}
                          disabled
                        />
                      </div>

                      {/* For faculty, show sub-role as designation (read-only) */}
                      {user?.role === 'FACULTY' && (
                        <div className="form-group">
                          <label>Designation</label>
                          <input
                            type="text"
                            className="form-input"
                            value={getFacultySubroleDisplay(formData.faculty_subrole)}
                            disabled
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Account Status */}
                <div className="profile-section">
                  <h3 className="section-title">Account Status</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Approval Status</label>
                      <div>
                        <span className={`badge ${
                          user?.approval_status === 'APPROVED' ? 'badge-green' :
                          user?.approval_status === 'PENDING' ? 'badge-yellow' :
                          'badge-red'
                        }`}>
                          {user?.approval_status}
                        </span>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Member Since</label>
                      <input
                        type="text"
                        className="form-input"
                        value={
                          user?.created_at
                            ? new Date(user.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })
                            : 'N/A'
                        }
                        disabled
                      />
                    </div>
                  </div>
                </div>

                {/* Bank Details Section - visible to all roles except STUDENT */}
                {user?.role !== 'STUDENT' && (
                  <div className="profile-section">
                    <h3 className="section-title">Bank Details</h3>
                    {bankMessage.text && (
                      <div className={`alert ${bankMessage.type === 'success' ? 'alert-success' : 'alert-error'}`}
                        style={{ marginBottom: '1rem' }}>
                        {bankMessage.text}
                      </div>
                    )}
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Account Holder Name</label>
                        <input
                          type="text"
                          name="account_holder_name"
                          className="form-input"
                          value={bankData.account_holder_name}
                          onChange={handleBankChange}
                          disabled={!isEditing}
                          placeholder="As per bank records"
                        />
                      </div>

                      <div className="form-group">
                        <label>Bank Name</label>
                        <input
                          type="text"
                          name="bank_name"
                          className="form-input"
                          value={bankData.bank_name}
                          onChange={handleBankChange}
                          disabled={!isEditing}
                          placeholder="e.g., State Bank of India"
                        />
                      </div>

                      <div className="form-group">
                        <label>Account Number</label>
                        <input
                          type="text"
                          name="account_number"
                          className="form-input"
                          value={bankData.account_number}
                          onChange={handleBankChange}
                          disabled={!isEditing}
                          placeholder="Bank account number"
                        />
                      </div>

                      <div className="form-group">
                        <label>IFSC Code</label>
                        <input
                          type="text"
                          name="ifsc_code"
                          className="form-input"
                          value={bankData.ifsc_code}
                          onChange={handleBankChange}
                          disabled={!isEditing}
                          placeholder="e.g., SBIN0001234"
                          style={{ textTransform: 'uppercase' }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Branch</label>
                        <input
                          type="text"
                          name="branch"
                          className="form-input"
                          value={bankData.branch}
                          onChange={handleBankChange}
                          disabled={!isEditing}
                          placeholder="Branch name and city"
                        />
                      </div>
                    </div>

                    {/* Separate save button for bank details */}
                    {isEditing && (
                      <div style={{ marginTop: '1rem' }}>
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={handleBankSubmit}
                          disabled={bankLoading}
                        >
                          {bankLoading ? 'Saving Bank Details...' : 'Save Bank Details'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ProfilePage;